// « Comparer à » dans un vrai cadre — lot B du CDC du 09/10 (M2), réponses de Marc du 11/10
// (Q2 : « pastilles oui, export SVG inchangé »).
//
// Avant : une ligne de flex sans bordure ni fond, le mot « Comparer à », un sélecteur et des
// puces — rien ne disait où commençait et où finissait la comparaison, ni quelle courbe était
// quelle commune. Désormais un seul composant, aux trois endroits (barres et courbes,
// pyramide, synthèse) :
//   · un `fieldset` légendé « Comparer à » — le cadre ET le nom du groupe pour un lecteur
//     d'écran ;
//   · la puce fixe « Ma commune », puis une puce par échelle ou commune ajoutée, chacune
//     avec la PASTILLE de la couleur de sa série dans le graphique (fournie par l'appelant,
//     qui la tient de la même fonction que Chart.js — jamais une palette recopiée) ;
//   · le sélecteur « + ajouter… » (passé en enfant : ses règles — plafond, doublons retirés,
//     attente — restent celles de l'appelant, PR #672) ;
//   · en dessous, en 11,5 px gris, la règle du plafond et les messages (attente, erreurs).
//
// Replié : sur une carte étroite (largeur normale de la grille), le cadre tient en une ligne
// « Comparer à : Ma commune · Doubs · France + ajouter… » ; il se déploie dès que la carte
// est large (lot A : « Large », « Pleine largeur », plein écran). Rien ne se cache derrière
// un clic : replié ou déployé, ce sont les mêmes boutons et le même sélecteur, au clavier
// comme à la souris — seule la mise en forme change. La règle, retirée de la vue en mode
// replié, reste lue par le lecteur d'écran (reliée au sélecteur par `aria-describedby`).
//
// L'export SVG / PNG du graphique ne voit pas ce cadre : il rejoue l'instance Chart.js
// retrouvée par son canvas (`partage/rejeu.ts`) — les séries ajoutées y sont, le DOM autour
// non (Q2 : voulu).
import {
  Children, cloneElement, isValidElement, useId, useLayoutEffect, useRef, useState,
  type ReactElement, type ReactNode,
} from 'react'
import './ComparerA.css'

/** La couleur d'une série dans le graphique ; `contour` = tracé seul (pyramide comparée). */
export interface PastilleSerie { couleur: string; contour?: boolean }

export interface PuceComparee {
  cle: string
  libelle: string
  /** Aucune pastille quand la cible n'a pas de couleur propre (barre d'un empilé 100 %). */
  pastilles?: PastilleSerie[]
  /** Présent → une croix « Retirer {libelle} ». */
  onRetirer?: () => void
  /** Présent → le libellé est un bouton bascule (barre affichée / masquée). */
  onBasculer?: () => void
  actif?: boolean
  /** `false` tant que la cible n'est pas tracée (référent de la pyramide en chargement, en
   *  erreur ou sans tranches) : sur papier, la légende ne nomme que ce que le graphique
   *  montre (revue codex, P2). Absent = tracée. */
  tracee?: boolean
}

export interface ComparerAProps {
  /** La puce fixe — omise là où la commune n'est pas une série (synthèse, empilés). */
  principale?: { libelle?: string; pastilles?: PastilleSerie[] }
  puces?: PuceComparee[]
  /** Le sélecteur (ou les sélecteurs) d'ajout, avec leur propre logique. */
  children?: ReactNode
  /** La règle du plafond, dérivée des constantes réelles par l'appelant. */
  regle?: string
  /** Attente, plafond atteint, erreurs — rendus sous la ligne, dans les deux modes. */
  messages?: ReactNode
  /** Forcé ; sinon mesuré sur la largeur du cadre. */
  compact?: boolean
  /** Largeur (px) sous laquelle le cadre se replie : une colonne de `.obs2-grid`. */
  seuilCompact?: number
}

/** Une carte « Normale » de la grille fait 330 à ~540 px ; « Large » en fait le double. */
const SEUIL_COMPACT = 560

function Pastilles({ pastilles }: { pastilles?: PastilleSerie[] }) {
  if (!pastilles?.length) return null
  return (
    <span className="obs2-comparer-pastilles" aria-hidden="true">
      {pastilles.map((p, i) => (
        <span key={i}
              className={`obs2-comparer-pastille${p.contour ? ' obs2-comparer-pastille--contour' : ''}`}
              style={p.contour ? { borderColor: p.couleur } : { backgroundColor: p.couleur }} />
      ))}
    </span>
  )
}

export default function ComparerA({ principale, puces = [], children, regle, messages,
                                    compact, seuilCompact = SEUIL_COMPACT }: ComparerAProps) {
  const ref = useRef<HTMLFieldSetElement | null>(null)
  const idRegle = useId()
  const [mesureCompact, setMesureCompact] = useState(false)
  // La BORDURE du cadre est mesurée, pas son contenu : la largeur d'un bloc ne dépend pas de
  // ce qu'il contient (`min-width: 0` dans le CSS), et le padding, qui change d'un mode à
  // l'autre, n'entre pas dans la décision — pas d'oscillation au seuil. Une largeur nulle
  // (graphique masqué en vue tableau, onglet en arrière-plan) ne décide rien.
  useLayoutEffect(() => {
    if (compact !== undefined) return
    const el = ref.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const mesurer = () => {
      const l = el.getBoundingClientRect().width
      if (l > 0) setMesureCompact(l < seuilCompact)
    }
    mesurer()
    const ro = new ResizeObserver(mesurer)
    ro.observe(el)
    return () => ro.disconnect()
  }, [compact, seuilCompact])
  const estCompact = compact ?? mesureCompact

  // La règle décrit le sélecteur : reliée par `aria-describedby`, elle reste lue même quand
  // le mode replié la retire de la vue.
  const selecteurs = regle
    ? Children.map(children, c => isValidElement(c) && c.type === 'select'
        ? cloneElement(c as ReactElement<{ 'aria-describedby'?: string }>,
                       { 'aria-describedby': idRegle })
        : c)
    : children
  // À l'impression (CDC « témoins partout », étape 8), le cadre sert de légende aux séries
  // ajoutées : il nomme chaque commune comparée avec la pastille de sa couleur. Une cible
  // ajoutée est celle qu'on peut retirer (`onRetirer`), et elle ne compte que si elle est
  // tracée (`tracee`, revue codex P2) ; sans elle, le cadre ne dirait que
  // « Ma commune » ou les barres d'origine, déjà lisibles sur le graphique, et la feuille
  // l'éteint sur papier (`.obs2-comparer--vide`, `ComparerA.css`).
  const vide = !puces.some(p => p.onRetirer && p.tracee !== false)

  return (
    <fieldset ref={ref} className={`obs2-comparer${estCompact ? ' obs2-comparer--compact' : ''}${
      vide ? ' obs2-comparer--vide' : ''}`}>
      <legend className="obs2-comparer-legende">Comparer à</legend>
      <div className="obs2-comparer-ligne">
        {estCompact && <span className="obs2-comparer-titre" aria-hidden="true">Comparer à :</span>}
        {principale && (
          <span className="obs2-comparer-puce obs2-comparer-puce--fixe">
            <Pastilles pastilles={principale.pastilles} />
            {principale.libelle ?? 'Ma commune'}
          </span>
        )}
        {puces.map(p => (
          <span key={p.cle}
                className={`obs2-comparer-puce${p.onBasculer && !p.actif ? ' obs2-comparer-puce--off' : ''}${
                  p.tracee === false ? ' obs2-comparer-puce--non-tracee' : ''}`}>
            {p.onBasculer ? (
              <button type="button" className="obs2-comparer-bascule" aria-pressed={!!p.actif}
                      onClick={p.onBasculer}>
                <Pastilles pastilles={p.pastilles} />
                {p.libelle}
              </button>
            ) : (
              <>
                <Pastilles pastilles={p.pastilles} />
                {p.libelle}
              </>
            )}
            {p.onRetirer && (
              <button type="button" className="obs2-comparer-retirer"
                      aria-label={`Retirer ${p.libelle}`} onClick={p.onRetirer}>
                ✕
              </button>
            )}
          </span>
        ))}
        {selecteurs}
      </div>
      {(regle || messages) && (
        <div className="obs2-comparer-notes">
          {regle && <span id={idRegle} className="obs2-comparer-regle">{regle}</span>}
          {messages}
        </div>
      )}
    </fieldset>
  )
}
